<template>
  <div class="fleet-dispatcher-map">
    <div :id="mapID" class="map" />
  </div>
</template>

<script>
import L from "leaflet";
import "leaflet.awesome-markers";

// import geo from "@/geo";
import vehicles from "@/vehicles";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

export default {
  name: "FleetDispatcherMap",
  props: {
    workflow: Object,
  },
  data() {
    return {
      mapID: "fleet-dispatcher-map",
      map: null,
      layer: null, // main layer
      userMarker: null,
      spin: false,
      driversMarkers: [],
      jobsMarkers: [],
      popupWide: { closeButton: false, maxWidth: 300, autoPan: false },
      // popupNarrow: { closeButton: false, maxWidth: 80, autoPan: false },
    };
  },
  computed: {
    vehicleImg() {
      var iconName = this.workflow.data.icon || this.workflow.name;
      return vehicles.img(iconName);
    },
    jobs() {
      return this.$store.state.ongoingDispatcherTransactions;
    },
  },
  created() {
    L.AwesomeMarkers.Icon.prototype.options.prefix = "fa";
  },
  mounted() {
    this.initMap();
    this.updateDrivers();
    this.drawJobs();
    this.watchUserLocation();
    this.watchTransactions();
  },
  activated() {
    this.updateUserMarker();
  },
  methods: {
    watchUserLocation() {
      this.$store.subscribe((mutation) => {
        if (mutation.type === "setUserState") {
          this.warning = null;
          var coords = this.$store.getters.getUserLatLon;
          this.userMarker.setLatLng(coords);
        }
      });
    },
    watchTransactions() {
      this.$store.subscribe((mutation) => {
        if (mutation.type === "setOngoingDispatcherTransactions") {
          this.drawJobs();
        }
      });
    },
    recenter() {
      this.spin = true;
      setTimeout(() => (this.spin = false), 600);
      this.updateUserMarker(true);
    },
    initMap() {
      var tilesURL = this.$t("geo.map-tiles"); // language specific maps

      this.map = L.map(this.mapID, { attributionControl: false });
      var coords = this.$store.getters.getUserLatLonWithFallback;
      this.map.setView(coords, 16);

      L.tileLayer(tilesURL, {
        tileSize: 512,
        zoomOffset: -1,
        // minZoom: 12,
        maxZoom: 19,
      }).addTo(this.map);

      L.control.attribution({ position: "topright" }).addTo(this.map);

      this.layer = L.featureGroup().addTo(this.map);

      this.map.zoomControl.remove();
      this.drawUserLocation();
    },
    drawUserLocation() {
      var coords = this.$store.getters.getUserLatLon;
      var icon = L.divIcon({
        html: `<div class="blue-dot pulse"></div>`,
        popupAnchor: [7, -6],
        className: "none",
      });
      this.userMarker = L.marker(coords, { icon: icon });
      this.userMarker.addTo(this.map);
      var popupOpts = { closeButton: false, autoPan: true };
      this.userMarker.bindPopup(this.$t("ride.your-current-gps"), popupOpts);
    },
    updateUserMarker(flyTo) {
      var coords = this.$store.getters.getUserLatLon;
      if (!coords) return;
      this.userMarker.setLatLng(coords);
      if (flyTo) this.map.flyTo(coords);
      else this.map.setView(coords, 17);
    },
    updateDrivers() {
      this.$store
        .dispatch("trackDrivers", this.workflow.id)
        .then((driverStates) => {
          if (!driverStates || !driverStates.length) {
            this.errorToast("No Active Drivers", "top", 9000);
          } else this.drawDrivers(driverStates);
        })
        .catch((err) => this.apiError(err)); // err.response.data === "No active workers"
    },
    drawDrivers(drivers) {
      console.log("drivers", drivers);
      this.removeDrivers();
      var availableIcon = L.divIcon({
        html: `<div class='driver-circle available'><img src='${this.vehicleImg}' /></div>`,
        popupAnchor: [-5, -20],
        className: "none",
      });
      var busyIcon = L.divIcon({
        html: `<div class='driver-circle busy'><img src='${this.vehicleImg}' /></div>`,
        popupAnchor: [-5, -20],
        className: "none",
      });
      var available = { icon: availableIcon, draggable: true };
      var busy = { icon: busyIcon, draggable: true };

      drivers.forEach((ds) => {
        if (ds.role.role_name === "Dispatcher") return; // skip dispatcher
        // if (ds.lat == 0) return; // skip bad driver location
        var t = (new Date() - new Date(ds.updated_at)) / (1000 * 60); // minutes ago
        if (t < 0) return 0;
        // if (t > 240) return; // don't show if older than an hour or 4 hours
        var options = available;
        if (ds.role.data.is_busy) options = busy;
        var m = L.marker([ds.lat, ds.lon], options).addTo(this.layer);
        var profPic = this.profPic(ds.user);
        var img = `<img src=${profPic} style="object-fit: cover; height: 90px; width: 90px; border-radius:8px" />`;
        var name = `<strong>${ds.user.name}</strong>`;
        var content = `<div style="width:90px">${img}<br>${name}
                      <br>${Math.round(t)} mins ago</div>`;
        m.bindPopup(content, this.popupWide);
        this.driversMarkers.push(m);
      });
      if (drivers) setTimeout(() => this.fitToDrivers(), 600);
    },
    removeDrivers() {
      if (!this.driversMarkers.length) return;
      this.driversMarkers.forEach((m) => this.layer.removeLayer(m)); // first remove previous markers
    },
    coloredIcon(state) {
      var color = "blue";
      if (state === "initiated") color = "orange";
      if (state === "confirmed") color = "darkblue";
      if (state === "cancelled_by_passenger") color = "red";
      if (state === "driver_arrived") color = "green";
      // if (state === "enroute") color = "blue"; // default color?
      // this.console("coloredIcon", state, color);
      return L.AwesomeMarkers.icon({
        icon: "street-view",
        markerColor: color,
      });
    },
    drawJobs() {
      console.log("draw jobs", this.jobs);
      this.removeJobs();
      // var iconClass = "street-view";
      // if (j.data.type === "delivery") iconClass = "store-alt";
      // var pickupIcon = L.AwesomeMarkers.icon({
      //   icon: iconClass, //"arrow-up",
      //   markerColor: "blue",
      //   iconColor: "yellow",
      //   draggable: true,
      // });
      this.jobs.forEach((j) => {
        // var states = ["initiated"];
        if (j.state === "enroute") return;
        var pickupCoords = [j.data.pickup_stop.lat, j.data.pickup_stop.lon];
        // var icon = { icon: pickupIcon };
        var icon = { icon: this.coloredIcon(j.state), draggable: true };
        var m = L.marker(pickupCoords, icon).addTo(this.layer);
        var pickupText = this.$t("transport.pickup-point");
        var text = `<i style='color:grey'>${pickupText}</i><br>${j.data.pickup_stop.name}`;
        var profPic = this.profPic(j.customer);
        var img = `<img src=${profPic} style="object-fit: cover; height: 90px; width: 90px; border-radius:8px" />`;
        var name = `<strong>${j.customer.name}</strong>`;
        var content = `<div style="width:90px">${img}<br>${name}
                      <br>${text}</div>`;
        m.bindPopup(content, this.popupWide);
        // m.bindPopup(text, this.popupWide);
        this.jobsMarkers.push(m);
      });
      setTimeout(() => this.fitToDrivers(), 600);
    },
    removeJobs() {
      if (!this.jobsMarkers.length) return;
      this.jobsMarkers.forEach((m) => this.layer.removeLayer(m)); // first remove previous markers
    },
    fitToDrivers() {
      if (!this.layer.getLayers().length) return;
      this.map.flyToBounds(this.layer.getBounds(), { padding: [50, 50] });
    },
  },
};
</script>

<style scoped>
.map {
  position: absolute;
  top: 0;
  left: 0;
  height: calc(100vh - 190px);
  width: 100vw;
  z-index: 0;
}

.warning {
  font-weight: 450;
  position: absolute;
  top: 12px;
  width: 100vw;
  text-align: center;
  /* font-size: 0.8em; */
}

.center-btn {
  margin: 10px;
  padding: 0 9px;
  pointer-events: auto;
  /* color: rgb(0, 106, 255); */
  color: grey;
  box-shadow: 0 0 4px grey;
}
</style>

<style lang="scss">
#fleet-dispatcher-map {
  .leaflet-popup-content {
    padding: 5px 5px;
    margin: 1px 0;
    // color: rgb(23, 23, 193);
    color: rgb(23, 102, 193);
    font-size: 1.2em;
    // max-width: 130px;
  }

  .leaflet-popup-content i {
    font-weight: 500;
    opacity: 0.8;
    color: grey;
  }

  .blue-dot {
    margin-left: 6px;
    height: 15px;
    width: 15px;
    border-radius: 50%;
    background-color: #2693da;
    opacity: 0.9;
    border: 1px solid #feeaa4b5;
  }

  .pulse {
    animation: pulsate-1 3s ease-out infinite;
  }

  @keyframes pulsate-1 {
    0% {
      box-shadow: 0 0 0 1px cyan;
    }

    50% {
      box-shadow: 0 0 0 6px rgba(255, 217, 0, 0.5);
    }

    100% {
      box-shadow: 0 0 0 1px lightskyblue;
    }
  }

  .available {
    background-color: rgba(159, 255, 155, 0.7);
  }

  .busy {
    background-color: rgba(255, 94, 201, 0.7);
  }

  .driver-circle img {
    height: 24px;
    width: 24px;
    filter: drop-shadow(1px 1px 1px #555);
  }

  // .driver-circle img {
  //   height: 30px;
  //   width: 30px;
  //   filter: drop-shadow(1px 1px 1px #555);
  // }
  .driver-circle {
    height: 32px;
    width: 32px;
    border-radius: 50%;
    padding: 3px;
    transform: translateX(-50%) translateY(-50%);
    filter: drop-shadow(2px 2px 2px #555);
    animation: pulsate-yellow 1.5s 999;
  }
}
</style>